/**
 * The Vaarta Converse chat assistant, mounted once at the app root.
 *
 * Ported from the Social app's ChatbotWidget. The behaviour is deliberately
 * the same; the mechanics are not, because that one is a Next.js client
 * component (`next/script`, `usePathname`) and this is a webpack-built SPA.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { readAccessToken } from './utils/auth-token-store';
import { chatbotTokenUrl, converseConfig, isCandidatePage, isConverseConfigured, } from './utils/converse-config';
/** The signed-in user's id, read off the access token's own claims. */
const readCurrentUserId = () => {
    var _a, _b;
    const token = readAccessToken();
    if (!token)
        return null;
    try {
        const segment = token.split('.')[1];
        if (!segment)
            return null;
        const json = JSON.parse(atob(segment.replace(/-/g, '+').replace(/_/g, '/')));
        const id = (_b = (_a = json === null || json === void 0 ? void 0 : json.id) !== null && _a !== void 0 ? _a : json === null || json === void 0 ? void 0 : json.user_id) !== null && _b !== void 0 ? _b : json === null || json === void 0 ? void 0 : json.sub;
        return id != null ? String(id) : null;
    }
    catch (_c) {
        return null;
    }
};
const isTokenExpired = (token) => {
    try {
        const segment = token.split('.')[1];
        if (!segment)
            return true;
        const { exp } = JSON.parse(atob(segment.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof exp !== 'number' || exp * 1000 <= Date.now();
    }
    catch (_a) {
        return true;
    }
};
/**
 * The identity token Converse verifies, minted by our backend.
 *
 * Converse calls this again whenever the credential expires mid-conversation,
 * so it must fetch fresh every time rather than close over a token captured at
 * init — otherwise a session the app has since refreshed stops working.
 */
const fetchConverseToken = () => __awaiter(void 0, void 0, void 0, function* () {
    const accessToken = readAccessToken();
    if (!accessToken)
        throw new Error('No active session');
    const response = yield fetch(chatbotTokenUrl(), {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok)
        throw new Error('Failed to mint Converse token');
    const { token } = yield response.json();
    return token;
});
/**
 * The real IAM access token this app already sends on its own API calls,
 * forwarded to our MCP server so it can act as this user.
 *
 * This is NOT interchangeable with the token above, and conflating the two is
 * the single most common way this integration breaks: Converse cannot verify
 * an IAM signature, and IAM cannot verify a Converse one. Read live on every
 * call for the same reason as above — never memoise.
 */
const getMcpToken = () => __awaiter(void 0, void 0, void 0, function* () { return readAccessToken() || ''; });
export default function ConverseChatWidget() {
    const { pathname } = useLocation();
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const initializedUserIdRef = useRef(null);
    // The candidate's pages (the interview and slot selection) never get the recruiter
    // assistant, even when this browser also holds a recruiter session.
    const onCandidatePage = isCandidatePage(pathname);
    // Load the widget script once, and only when there is something to load.
    useEffect(() => {
        if (!isConverseConfigured() || onCandidatePage)
            return;
        const existing = document.querySelector(`script[src="${converseConfig.widgetUrl}"]`);
        if (existing) {
            setScriptLoaded(true);
            return;
        }
        const script = document.createElement('script');
        script.src = converseConfig.widgetUrl;
        script.async = true;
        script.onload = () => setScriptLoaded(true);
        script.onerror = () => console.error('Converse widget script failed to load');
        document.body.appendChild(script);
    }, [onCandidatePage]);
    useEffect(() => {
        if (!scriptLoaded || !window.Chatbot)
            return;
        const syncWidget = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g;
            // Arriving on a candidate page with the assistant open: close it, and do not start it.
            if (onCandidatePage) {
                if (initializedUserIdRef.current) {
                    (_a = window.Chatbot) === null || _a === void 0 ? void 0 : _a.destroy();
                    initializedUserIdRef.current = null;
                }
                return;
            }
            const accessToken = readAccessToken();
            const userId = readCurrentUserId();
            const isAuthenticated = !!accessToken && !!userId && !isTokenExpired(accessToken);
            if (!isAuthenticated) {
                if (initializedUserIdRef.current) {
                    (_b = window.Chatbot) === null || _b === void 0 ? void 0 : _b.destroy();
                    initializedUserIdRef.current = null;
                }
                return;
            }
            // Account switch: tear the previous session down first, so neither the
            // assistant nor our MCP carries the last user's identity forward.
            if (initializedUserIdRef.current &&
                initializedUserIdRef.current !== userId) {
                (_c = window.Chatbot) === null || _c === void 0 ? void 0 : _c.destroy();
                initializedUserIdRef.current = null;
            }
            if (initializedUserIdRef.current) {
                (_e = (_d = window.Chatbot) === null || _d === void 0 ? void 0 : _d.setContext) === null || _e === void 0 ? void 0 : _e.call(_d, { page: pathname });
                return;
            }
            try {
                const userToken = yield fetchConverseToken();
                const mcpToken = yield getMcpToken();
                yield window.Chatbot.init({
                    baseUrl: converseConfig.apiBaseUrl,
                    publicKey: converseConfig.publicKey,
                    userToken, // identity to Converse — not the IAM token
                    getToken: fetchConverseToken,
                    mcpToken, // our IAM token, forwarded to the MCP server as this user
                    getMcpToken,
                });
                initializedUserIdRef.current = userId;
                (_g = (_f = window.Chatbot) === null || _f === void 0 ? void 0 : _f.setContext) === null || _g === void 0 ? void 0 : _g.call(_f, { page: pathname });
            }
            catch (error) {
                console.error('Converse init failed', error);
            }
        });
        void syncWidget();
        // Re-runs on navigation as well as on script load, which is what makes the
        // widget appear for someone who signed in after this mounted, and what
        // catches an account switch. Keying only on scriptLoaded would leave the
        // assistant absent for the whole session after a login.
    }, [pathname, scriptLoaded]);
    useEffect(() => () => {
        if (initializedUserIdRef.current && window.Chatbot) {
            window.Chatbot.destroy();
            initializedUserIdRef.current = null;
        }
    }, []);
    return null;
}
